import React, { useRef } from 'react';
import Datatable from 'src/common/refactoredComponents/dataTable/DataTable';
import BasicPagination from 'src/common/refactoredComponents/paginator';
import TruncatedTooltip from 'src/common/organisms/listView/TruncatedTooltip';
import AlertCircleOutline from 'src/assets/images/AlertCircleOutline.svg';
import { isIpad, isLandscape } from 'src/utils/util';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import i18n from 'src/i18n.js';
import { sizes } from 'src/styles/Media.styled';

import {
    ConsignedTableContainer,
    CellPrimaryText,
    DescriptionTooltipWrapper,
    ExpiredUseByDateIcon,
    ExpiredUseByDateText,
    UseByDateCell,
    AccountCell,
    AccountNameText,
    AccountNumberText,
    PaginationContainer,
} from './ConsignedTableUI.styled';
import { isMobileView } from 'src/utils/util';
import ConsignedMobileView from '../consignedTableMobileView';

const DESCRIPTION_MAX_LENGTH = isIpad() ? 18 : 32;
const ACCOUNTNAME_MAX_LENGTH = isIpad() ? 18 : 25;
const IPAD_MINI_WIDTH = sizes.smalDevice;
const IPAD_AIR_WIDTH = sizes.ipadAir;

const getConsignedTableWidths = (viewportWidth = window.innerWidth) => {
    if (viewportWidth === IPAD_MINI_WIDTH) {
        return {
            account: '28%',
            cfn: '8%',
            description: '26%',
            batch: '12%',
            stock: '11%',
            sled: '15%',
        };
    }

    if (viewportWidth === IPAD_AIR_WIDTH) {
        return {
            account: '27%',
            cfn: '8%',
            description: '27%',
            batch: '11%',
            stock: '11%',
            sled: '16%',
        };
    }

    const isTabletView = viewportWidth >= sizes.smalDevice && viewportWidth <= sizes.mediumDevice;

    return {
        account: isTabletView ? '22%' : '26%',
        cfn: '10%',
        description: isTabletView ? '22%' : '28%',
        batch: isTabletView ? '15%' : '11%',
        stock: isTabletView ? '11%' : '10%',
        sled: isTabletView ? (isLandscape() ? '13%' : '16%') : '11%',
    };
};

const getValue = (rowData, fields, fallback = '-') => {
    const value = fields
        .map((field) => rowData?.[field])
        .find((fieldValue) => fieldValue !== undefined && fieldValue !== null && fieldValue !== '');

    return value === undefined || value === null || value === '' ? fallback : value;
};

const renderTextCell = (fields) => (rowData) => (
    <CellPrimaryText>{getValue(rowData, fields)}</CellPrimaryText>
);

const renderDescriptionCell = (fields) => (rowData) => {
    const description = getValue(rowData, fields);

    if (description === '-') {
        return <CellPrimaryText>{description}</CellPrimaryText>;
    }

    return (
        <CellPrimaryText>
            <DescriptionTooltipWrapper $maxLength={DESCRIPTION_MAX_LENGTH}>
                <TruncatedTooltip
                    text={String(description)}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    productCfn=""
                />
            </DescriptionTooltipWrapper>
        </CellPrimaryText>
    );
};

const formatUseByDate = (value, isLanguageChanged = false) => {
    if (!value || value === '-') {
        return value;
    }

    const stringValue = String(value);
    const isoDateParts = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (isoDateParts) {
        if (isLanguageChanged) {
            return `${isoDateParts[1]}/${isoDateParts[2]}/${isoDateParts[3]}`;
        }

        return `${isoDateParts[2]}/${isoDateParts[3]}/${isoDateParts[1]}`;
    }

    const parsedDate = new Date(stringValue);

    if (Number.isNaN(parsedDate.getTime())) {
        return stringValue;
    }

    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    const year = parsedDate.getFullYear();

    if (isLanguageChanged) {
        return `${year}/${month}/${day}`;
    }

    return `${month}/${day}/${year}`;
};

const renderUseByDateCell = (fields, isLanguageChanged = false) => (rowData) => {
    const useByDate = formatUseByDate(getValue(rowData, fields), isLanguageChanged);

    if (rowData?.expiryStatus === 'EXPIRED') {
        return (
            <UseByDateCell>
                <ExpiredUseByDateText>{useByDate}</ExpiredUseByDateText>
                <ExpiredUseByDateIcon
                    src={AlertCircleOutline}
                    alt="Expired"
                />
            </UseByDateCell>
        );
    }

    return <CellPrimaryText>{useByDate}</CellPrimaryText>;
};

const formatAccountNumber = (value) => String(value)
    .replace(/-/g, '')
    .replace(/^0+/, '');

const splitAccountValue = (value) => {
    const trimmedValue = String(value).trim();
    const accountParts = trimmedValue.match(/^([0-9-]+)\s*-?\s*(.*)$/);

    if (!accountParts) {
        return {
            accountNumber: formatAccountNumber(trimmedValue),
            accountName: '',
        };
    }

    return {
        accountNumber: formatAccountNumber(accountParts[1]),
        accountName: accountParts[2].trim(),
    };
};

const accountTemplate = (rowData) => {
    const accountValue = getValue(rowData, ['account'], '');
    const parsedAccount = accountValue ? splitAccountValue(accountValue) : null;
    const accountName = parsedAccount?.accountName || getValue(rowData, ['accountName', 'customerName'], '');
    const accountNumber = parsedAccount?.accountNumber || formatAccountNumber(getValue(rowData, ['accountNumber', 'accountCode', 'customerNumber'], ''));

    if (!accountNumber && !accountName) {
        return <CellPrimaryText>-</CellPrimaryText>;
    }

    return (
        <AccountCell>
            {accountNumber ? <AccountNumberText>{accountNumber}</AccountNumberText> : null}
            {accountName ? (
                <AccountNameText>
                    <DescriptionTooltipWrapper $maxLength={ACCOUNTNAME_MAX_LENGTH}>
                        <TruncatedTooltip
                            text={String(accountName)}
                            maxLength={ACCOUNTNAME_MAX_LENGTH}
                            productCfn=""
                        />
                    </DescriptionTooltipWrapper>
                </AccountNameText>
            ) : null}
        </AccountCell>
    );
};

const applyColumnStyles = (column) => ({
    ...column,
    headerStyle: {
        ...column.headerStyle,
        width: column.width,
    },
    bodyStyle: {
        ...column.bodyStyle,
        width: column.width,
    },
});

const getHeaderLabel = (t, key) => t(key).replace(/:\s*$/, '');

const getConsignedTableColumns = (t = i18n.t.bind(i18n), viewportWidth = window.innerWidth, isLanguageChanged = false) => {
    const widths = getConsignedTableWidths(viewportWidth);

    return [
        {
            field: 'partnerLocName',
            header: t('reportUsage.record.account.title'),
            width: widths.account,
            sortable: true,
            body: accountTemplate,
        },
        {
            field: 'cfn',
            header: getHeaderLabel(t, 'receiveSets.InventoryConsigned.cfn'),
            width: widths.cfn,
            sortable: true,
            body: renderTextCell(['cfn', 'productCFN', 'productCfn']),
        },
        {
            field: 'productDescription',
            header: t('scheduleSurgery.surgery.notifications.itemListView.description'),
            width: widths.description,
            sortable: true,
            body: renderDescriptionCell(['materialDesc', 'description', 'productDescription', 'itemDescription']),
        },
        {
            field: 'batch',
            header: t('reportUsage.record.reportPayment.ItemList.ReportPaymentItemListMobile.lotSerial'),
            width: widths.batch,
            headerClassName: 'consigned-batch-header',
            bodyClassName: 'consigned-batch-body',
            sortable: true,
            body: renderTextCell(['lotSerial', 'lotSerialNumber', 'lotNumber', 'serialNumber']),
        },
        {
            field: 'stock',
            header: t('receiveSets.InventoryConsigned.onHand'),
            width: widths.stock,
            headerStyle: { textAlign: 'center' },
            bodyStyle: { textAlign: 'center' },
            headerClassName: 'consigned-on-hand-header',
            bodyClassName: 'consigned-on-hand-body',
            sortable: true,
            body: renderTextCell(['onHand', 'onHandQuantity', 'quantityOnHand', 'quantity']),
        },
        {
            field: 'sled',
            header: t('receiveSets.InventoryConsigned.useByDate'),
            width: widths.sled,
            headerClassName: 'consigned-use-by-date-header',
            bodyClassName: 'consigned-use-by-date-body',
            sortable: true,
            body: renderUseByDateCell(['useByDate', 'expirationDate', 'expiryDate'], isLanguageChanged),
        },
    ].map(applyColumnStyles);
};

const columns = getConsignedTableColumns();
export default function ConsignedTableUI({
    data = [],
    totalRecords = data.length,
    first = 0,
    rows = 5,
    paginator = true,
    onPageChange,
    onSortChange,
    sortField,
    sortOrder,
    scrollHeight = 'flex',
    emptyMessage = 'No consigned inventory found.',
    paginationInfoText = 'Showing Products',
}) {
    const { t } = useTranslation();
    const { isLanguageChanged } = useSelector((state) => state.loginReducer);
    const isMobile = isMobileView();
    const tableContainerRef = useRef(null);

    const tableColumns = getConsignedTableColumns(t, window.innerWidth, isLanguageChanged);

    const scrollTableToTop = () => {
        const container = tableContainerRef.current;

        if (!container) return;

        [
            container,
            container.querySelector('.p-datatable-wrapper'),
            container.querySelector('.p-datatable-scrollable-body'),
        ].forEach((scrollElement) => {
            if (scrollElement) {
                scrollElement.scrollTop = 0;
            }
        });
        container.scrollIntoView({ block: 'start', behavior: 'auto' });
    };

    const handlePageChange = (event) => {
        scrollTableToTop();
        onPageChange?.(event);
    };

    return (
        <ConsignedTableContainer 
            id="receiveSets-consigned-table-ui"
            ref={tableContainerRef}
            className={isMobile ? 'mobile-view' : 'desktop-view'}
            isMobile={isMobile}
        >
            {isMobile ? (
                <ConsignedMobileView
                    data={data}
                    totalRecords={totalRecords}
                    first={first}
                    rows={rows}
                    paginator={paginator}
                    onPageChange={handlePageChange}
                    onSortChange={onSortChange}
                    emptyMessage={emptyMessage}
                    paginationInfoText={paginationInfoText}
                />
            ) : (
                <>
                    <Datatable
                        id="receiveSets-consigned-table"
                        backgroundColorOdd="#FFFFFF"
                        backgroundColorEven="#F5F5F5"
                        backgroundColorPagination="#F5F5F5"
                        columns={tableColumns}
                        data={data}
                        rows={rows}
                        first={first}
                        totalRecords={totalRecords}
                        paginator={false}
                        onPageChange={handlePageChange}
                        onSort={onSortChange}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        scrollHeight={scrollHeight}
                        emptyMessage={emptyMessage}
                        isListView
                        displayType="inline-table"
                        height="100px"
                        rightCOntentMarginTop={5}
                        paginationInfoText={paginationInfoText}
                    />
                    {paginator && totalRecords > 0 && (
                        <PaginationContainer>
                            <BasicPagination
                                alignSelf="center"
                                totalRecords={totalRecords}
                                recordsPerPage={rows}
                                recordsFetched={data?.length > 0 ? data.length : 0}
                                pageNumber={Math.floor(first / rows)}
                                handleChange={handlePageChange}
                                first={first}
                                flexDirection="row"
                                ipadPopupNoMarginRight
                                background="#FFFFFF"
                                content={paginationInfoText}
                                iPadPaginatorInfoWidth="100%"
                                iPadPaginatorWidth="100%"
                                linkPadding="8px 0"
                            />
                        </PaginationContainer>
                    )}
                </>
            )}
        </ConsignedTableContainer>
    );
}

export { columns as consignedTableColumns, getConsignedTableColumns };